import { Button, Loading } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { type ReactNode, useEffect, useRef } from 'react';

import * as styles from './permission-review-list.css';

/** One persistent search field, a bounded page, and distinct unknown/empty/error states. */
export function PermissionReviewList({
  search,
  hideSearch = false,
  emptyMessage,
  onSearchChange,
  placeholder,
  loading,
  error,
  offline,
  onRetry,
  total,
  start,
  count,
  onPrevious,
  onNext,
  children,
  testId,
}: {
  search: string;
  hideSearch?: boolean;
  emptyMessage?: ReactNode;
  onSearchChange(value: string): void;
  placeholder: string;
  loading: boolean;
  error?: boolean;
  offline?: boolean;
  onRetry(): void;
  total: number | null;
  start: number;
  count: number;
  onPrevious?: () => void;
  onNext?: () => void;
  children: ReactNode;
  testId: string;
}) {
  const t = useI18n();
  const list = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (list.current) list.current.scrollTop = 0;
  }, [search, start]);
  return (
    <div>
      {!hideSearch ? (
        <div className={styles.toolbar}>
          <input
            type="search"
            disabled={offline}
            className={styles.search}
            value={search}
            onChange={event => onSearchChange(event.target.value)}
            placeholder={placeholder}
            aria-label={placeholder}
            data-testid={`${testId}-search`}
          />
        </div>
      ) : null}
      {offline || error ? (
        <div className={styles.notice} role={error ? 'alert' : 'status'}>
          <span>
            {t[
              offline
                ? 'com.streamlain.permissions.review.offline'
                : 'com.streamlain.permissions.review.error'
            ]()}
          </span>
          <Button
            variant="plain"
            disabled={loading || offline}
            onClick={onRetry}
          >
            {t['com.streamlain.permissions.review.retry']()}
          </Button>
        </div>
      ) : null}
      {loading && total === null ? (
        <div className={styles.loadingNotice} role="status">
          <Loading size={14} />
          <span>{t['com.streamlain.permissions.review.loading']()}</span>
        </div>
      ) : null}
      {/* A refresh keeps the current rows in place and marks them busy, so
          the spinner never moves the content around. */}
      <div className={styles.listFrame}>
        <div
          ref={list}
          className={styles.list}
          aria-busy={loading}
          data-refreshing={loading && count > 0}
          data-testid={`${testId}-list`}
        >
          {children}
        </div>
        {loading && total !== null && count > 0 ? (
          <div
            className={styles.refreshing}
            role="status"
            aria-label={t['com.streamlain.permissions.review.loading']()}
            data-testid={`${testId}-refreshing`}
          >
            <Loading size={14} />
          </div>
        ) : null}
      </div>
      {!error && !offline && total !== null && count === 0 ? (
        <div className={styles.notice} role="status">
          {loading ? (
            <>
              <Loading size={14} />
              <span>{t['com.streamlain.permissions.review.loading']()}</span>
            </>
          ) : (
            (emptyMessage ?? t['com.streamlain.permissions.review.empty']())
          )}
        </div>
      ) : null}
      {total !== null && total > 0 && (onPrevious || onNext) ? (
        <div className={styles.pagination}>
          <span aria-live="polite">
            {t['com.streamlain.permissions.review.range']({
              start: String(count ? start + 1 : 0),
              end: String(start + count),
              total: String(total),
            })}
          </span>
          <div className={styles.actions}>
            <Button
              variant="plain"
              disabled={!onPrevious || loading || offline}
              onClick={onPrevious}
            >
              {t['com.streamlain.permissions.review.previous']()}
            </Button>
            <Button
              variant="plain"
              disabled={!onNext || loading || offline}
              onClick={onNext}
            >
              {t['com.streamlain.permissions.review.next']()}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
